/**
 * GRC theme colors — Cyber Mode token names, current-app defaults.
 * Pattern: picker → :root CSS vars → localStorage cache → API (when reachable).
 * DB keys have NO "--" prefix (matches backend whitelist).
 * Every token (hex and rgba overlays) is persisted with Save.
 */

import axios from "axios";

/** Short DB / API keys (no -- prefix). */
export type ThemeColorKey =
  | "primary"
  | "hover"
  | "pressed"
  | "selected"
  | "border"
  | "bg"
  | "surface"
  | "surface-2"
  | "gray"
  | "dark"
  | "light"
  | "text-primary"
  | "text-secondary"
  | "text-disabled"
  | "text-on-primary"
  | "text-muted"
  | "danger"
  | "danger-hover"
  | "danger-soft"
  | "warning"
  | "warning-soft"
  | "success"
  | "success-soft"
  | "info"
  | "info-soft"
  | "priority-low-text"
  | "priority-low-bg"
  | "priority-medium-text"
  | "priority-medium-bg"
  | "priority-high-text"
  | "priority-high-bg"
  | "priority-critical-text"
  | "priority-critical-bg"
  | "sidebar-profile-bg"
  | "sidebar-link-color"
  | "sidebar-link-active-bg"
  | "modal-overlay-bg"
  | "swatch-label-bg"
  | "swatch-label-bg-muted"
  | "swatch-label-bg-brand";

export type ThemeColors = Record<ThemeColorKey, string>;

/** Solid hex tokens persisted to POST /system-setting/theme-colors. */
export const THEME_COLOR_API_KEYS: ThemeColorKey[] = [
  "primary",
  "hover",
  "pressed",
  "selected",
  "border",
  "bg",
  "surface",
  "surface-2",
  "gray",
  "dark",
  "light",
  "text-primary",
  "text-secondary",
  "text-disabled",
  "text-on-primary",
  "text-muted",
  "danger",
  "danger-hover",
  "danger-soft",
  "warning",
  "warning-soft",
  "success",
  "success-soft",
  "info",
  "info-soft",
  "priority-low-text",
  "priority-low-bg",
  "priority-medium-text",
  "priority-medium-bg",
  "priority-high-text",
  "priority-high-bg",
  "priority-critical-text",
  "priority-critical-bg",
  "sidebar-link-active-bg",
];

/** Kept for callers; structural colors are saved with the rest of the palette. */
export const THEME_COLOR_FIXED_KEYS: ThemeColorKey[] = [];

/** Overlay tokens (often rgba). Persisted with the rest of the palette. */
export const THEME_COLOR_LOCAL_KEYS: ThemeColorKey[] = [
  "sidebar-profile-bg",
  "sidebar-link-color",
  "modal-overlay-bg",
  "swatch-label-bg",
  "swatch-label-bg-muted",
  "swatch-label-bg-brand",
];

export const THEME_COLOR_KEYS: ThemeColorKey[] = [
  ...THEME_COLOR_API_KEYS,
  ...THEME_COLOR_FIXED_KEYS,
  ...THEME_COLOR_LOCAL_KEYS,
];

/** Defaults match tokens.css / current app — no visual change until edited. */
export const GRC_THEME_DEFAULTS: ThemeColors = {
  primary: "#44225c",
  hover: "#5a2f78",
  pressed: "#361a49",
  selected: "#ede4f5",
  border: "#ededed",
  bg: "#F5F5F5",
  surface: "#ffffff",
  "surface-2": "#f9f9fb",
  gray: "#6c757d",
  dark: "#1f2f3e",
  light: "#f5f6f9",
  "text-primary": "#1f2f3e",
  "text-secondary": "#848789",
  "text-disabled": "#999999",
  "text-on-primary": "#ffffff",
  "text-muted": "#6b7280",
  danger: "#f1523d",
  "danger-hover": "#d43d2a",
  "danger-soft": "#e2b6b6",
  warning: "#f8aa4b",
  "warning-soft": "#ebdcb3",
  success: "#255f0b",
  "success-soft": "#b6caae",
  info: "#44225c",
  "info-soft": "#d0b5e3",
  "priority-low-text": "#027a48",
  "priority-low-bg": "#e7f6ec",
  "priority-medium-text": "#e68f1d",
  "priority-medium-bg": "#fff4c9",
  "priority-high-text": "#d54c1a",
  "priority-high-bg": "#ffdfd1",
  "priority-critical-text": "#cf2d20",
  "priority-critical-bg": "#ffdbd8",
  "sidebar-profile-bg": "rgba(255, 255, 255, 0.15)",
  "sidebar-link-color": "rgba(255, 255, 255, 0.85)",
  "sidebar-link-active-bg": "#6e5581",
  "modal-overlay-bg": "rgba(15, 23, 42, 0.5)",
  "swatch-label-bg": "rgba(0, 0, 0, 0.25)",
  "swatch-label-bg-muted": "rgba(0, 0, 0, 0.06)",
  "swatch-label-bg-brand": "rgba(68, 34, 92, 0.1)",
};

export const THEME_COLOR_META: Record<
  ThemeColorKey,
  { label: string; hint: string }
> = {
  primary: { label: "Primary", hint: "Brand / buttons / links" },
  hover: { label: "Hover", hint: "Primary hover state" },
  pressed: { label: "Pressed", hint: "Primary active / pressed" },
  selected: { label: "Selected", hint: "Selected / tint fill" },
  border: { label: "Border", hint: "Borders / dividers" },
  bg: { label: "Background", hint: "App page background" },
  surface: { label: "Surface", hint: "Cards / panels" },
  "surface-2": { label: "Surface 2", hint: "Subtle fills / zebra" },
  gray: { label: "Gray", hint: "Secondary / muted chrome" },
  dark: { label: "Dark", hint: "Dark surfaces / emphasis" },
  light: { label: "Light", hint: "Light panel fill" },
  "text-primary": { label: "Text primary", hint: "Body text" },
  "text-secondary": { label: "Text secondary", hint: "Muted copy" },
  "text-disabled": { label: "Text disabled", hint: "Disabled / placeholder" },
  "text-on-primary": { label: "Text on primary", hint: "Text on solid brand" },
  "text-muted": { label: "Text muted", hint: "Helper / caption" },
  danger: { label: "Danger", hint: "Destructive actions" },
  "danger-hover": { label: "Danger hover", hint: "Danger button hover" },
  "danger-soft": { label: "Danger soft", hint: "Danger tint fill" },
  warning: { label: "Warning", hint: "Warning status" },
  "warning-soft": { label: "Warning soft", hint: "Warning tint fill" },
  success: { label: "Success", hint: "Success status" },
  "success-soft": { label: "Success soft", hint: "Success tint fill" },
  info: { label: "Info", hint: "Info status" },
  "info-soft": { label: "Info soft", hint: "Info tint fill" },
  "priority-low-text": { label: "Low text", hint: "Priority low" },
  "priority-low-bg": { label: "Low background", hint: "Priority low fill" },
  "priority-medium-text": { label: "Medium text", hint: "Priority medium" },
  "priority-medium-bg": { label: "Medium background", hint: "Priority medium fill" },
  "priority-high-text": { label: "High text", hint: "Priority high" },
  "priority-high-bg": { label: "High background", hint: "Priority high fill" },
  "priority-critical-text": { label: "Critical text", hint: "Priority critical" },
  "priority-critical-bg": { label: "Critical background", hint: "Priority critical fill" },
  "sidebar-profile-bg": { label: "Profile chip", hint: "Sidebar profile (local)" },
  "sidebar-link-color": { label: "Nav link", hint: "Sidebar link text (local)" },
  "sidebar-link-active-bg": { label: "Nav active", hint: "Sidebar active link fill" },
  "modal-overlay-bg": { label: "Overlay", hint: "Modal scrim (local)" },
  "swatch-label-bg": { label: "Swatch label", hint: "Doc chrome (local)" },
  "swatch-label-bg-muted": { label: "Swatch muted", hint: "Light swatch label (local)" },
  "swatch-label-bg-brand": { label: "Swatch brand", hint: "Brand-tint label (local)" },
};

const STORAGE_KEY = "grc_theme_colors";

let publicLoadPromise: Promise<ThemeColors> | null = null;

export function cssVarName(key: ThemeColorKey): string {
  if (key.startsWith("priority-") || key.startsWith("sidebar-") || key.startsWith("modal-") || key.startsWith("swatch-")) {
    return `--${key}`;
  }
  return `--color-${key}`;
}

export function normalizeColor(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const v = value.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return `#${v.slice(1).toLowerCase()}`;
  if (/^#[0-9a-fA-F]{3}$/.test(v)) {
    return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`.toLowerCase();
  }
  if (/^#[0-9a-fA-F]{8}$/.test(v)) return `#${v.slice(1, 7)}`.toLowerCase();
  const rgba = v.match(
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/i
  );
  if (rgba) {
    const r = Math.round(Number(rgba[1]));
    const g = Math.round(Number(rgba[2]));
    const b = Math.round(Number(rgba[3]));
    const a = rgba[4] !== undefined ? Number(rgba[4]) : 1;
    if (a >= 1) {
      return `#${[r, g, b]
        .map((n) => n.toString(16).padStart(2, "0"))
        .join("")}`;
    }
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  return null;
}

function hexToRgbChannels(hex: string): string | null {
  const n = normalizeColor(hex);
  if (!n || !n.startsWith("#") || n.length !== 7) return null;
  const r = parseInt(n.slice(1, 3), 16);
  const g = parseInt(n.slice(3, 5), 16);
  const b = parseInt(n.slice(5, 7), 16);
  return `${r}, ${g}, ${b}`;
}

function syncBrowserThemeColor(primary: string): void {
  if (typeof document === "undefined") return;
  let meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "theme-color");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", primary);
}

export function getDefaultThemeColors(): ThemeColors {
  return { ...GRC_THEME_DEFAULTS };
}

export function mapRowToThemeColors(
  row: Record<string, any> | null | undefined
): ThemeColors | null {
  if (!row || typeof row !== "object" || Array.isArray(row)) return null;

  let raw: any = row.theme_colors ?? row.themeColors ?? null;

  // Some responses double-encode the map as a JSON string
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }

  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;

  const out: Partial<ThemeColors> = {};
  THEME_COLOR_KEYS.forEach((key) => {
    const hex = normalizeColor((raw as any)[key]);
    if (hex) out[key] = hex;
  });
  return Object.keys(out).length
    ? ({ ...GRC_THEME_DEFAULTS, ...out } as ThemeColors)
    : null;
}

export function readStoredThemeColors(): Partial<ThemeColors> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const out: Partial<ThemeColors> = {};
    THEME_COLOR_KEYS.forEach((key) => {
      const n = normalizeColor(parsed[key]);
      if (n) out[key] = n;
    });
    return Object.keys(out).length ? out : null;
  } catch {
    return null;
  }
}

export function getEffectiveThemeColors(): ThemeColors {
  const merged = { ...GRC_THEME_DEFAULTS, ...(readStoredThemeColors() || {}) };
  // Migrate previous translucent default to the solid Settings-controlled hex
  if (merged["sidebar-link-active-bg"] === "rgba(255, 255, 255, 0.12)") {
    merged["sidebar-link-active-bg"] = GRC_THEME_DEFAULTS["sidebar-link-active-bg"];
  }
  return merged;
}

export function persistThemeColorsCache(colors: ThemeColors): ThemeColors {
  const next: ThemeColors = { ...GRC_THEME_DEFAULTS };
  THEME_COLOR_KEYS.forEach((key) => {
    next[key] = normalizeColor(colors[key]) || GRC_THEME_DEFAULTS[key];
  });
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* quota / private mode */
  }
  return next;
}

export function clearStoredThemeColors(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Payload for POST — every theme token (hex and rgba overlays). */
export function toApiThemeColorsPayload(colors: ThemeColors): Record<string, string> {
  const out: Record<string, string> = {};
  THEME_COLOR_KEYS.forEach((key) => {
    const n = normalizeColor(colors[key]);
    out[key] = n || GRC_THEME_DEFAULTS[key];
  });
  return out;
}

export function applyThemeColors(
  colors: ThemeColors = getEffectiveThemeColors(),
  options: { useInline?: boolean } = {}
): ThemeColors {
  const useInline = options.useInline !== false;
  const root = document.documentElement;
  const next: ThemeColors = { ...GRC_THEME_DEFAULTS, ...colors };

  THEME_COLOR_KEYS.forEach((key) => {
    const varName = cssVarName(key);
    const value = normalizeColor(next[key]) || GRC_THEME_DEFAULTS[key];
    next[key] = value;
    if (useInline) {
      root.style.setProperty(varName, value);
    } else {
      root.style.removeProperty(varName);
    }
  });

  // Bridge Cuba legacy token + primary_color cache used by layout store
  if (useInline) {
    root.style.setProperty("--theme-default", next.primary);
    root.style.setProperty("--bs-primary", next.primary);
    // ABER-style aliases (LDAP pages: var(--brand)/var(--surface)/…)
    root.style.setProperty("--brand", next.primary);
    root.style.setProperty("--brand-d", next.pressed || next.primary);
    root.style.setProperty("--accent", next.primary);
    root.style.setProperty("--bg", next.bg);
    root.style.setProperty("--surface", next.surface);
    root.style.setProperty("--surface-2", next["surface-2"]);
    root.style.setProperty("--ink", next["text-primary"]);
    root.style.setProperty("--muted", next["text-secondary"]);
    root.style.setProperty("--line", next.border);
    const rgb = hexToRgbChannels(next.primary);
    if (rgb) {
      root.style.setProperty("--color-primary-rgb", rgb);
      root.style.setProperty("--bs-primary-rgb", rgb);
      root.style.setProperty("--brand-rgb", rgb);
      root.style.setProperty("--accent-rgb", rgb);
      root.style.setProperty("--v-theme-primary", rgb);
      root.style.setProperty("--v-theme-secondary", rgb);
    }
    const statusRgb: Array<[ThemeColorKey, string, string?]> = [
      ["danger", "--color-danger-rgb", "--v-theme-error"],
      ["warning", "--color-warning-rgb", "--v-theme-warning"],
      ["success", "--color-success-rgb", "--v-theme-success"],
      ["info", "--color-info-rgb", "--v-theme-info"],
    ];
    for (const [key, cssName, vTheme] of statusRgb) {
      const channels = hexToRgbChannels(next[key]);
      if (channels) {
        root.style.setProperty(cssName, channels);
        if (vTheme) root.style.setProperty(vTheme, channels);
      }
    }
    root.style.setProperty("--bs-danger", next.danger);
    root.style.setProperty("--bs-success", next.success);
    root.style.setProperty("--bs-warning", next.warning);
    root.style.setProperty("--bs-info", next.info);
    root.style.setProperty("--bs-body-bg", next.bg);
    root.style.setProperty("--bs-body-color", next["text-primary"]);
    root.style.setProperty("--bs-border-color", next.border);
    root.style.setProperty("--bs-card-bg", next.surface);
    root.style.setProperty("--bs-secondary", next.gray);
    try {
      localStorage.setItem("primary_color", next.primary);
    } catch {
      /* ignore */
    }
    syncBrowserThemeColor(next.primary);
  } else {
    root.style.removeProperty("--theme-default");
    root.style.removeProperty("--bs-primary");
    root.style.removeProperty("--color-primary-rgb");
    root.style.removeProperty("--bs-primary-rgb");
    root.style.removeProperty("--color-danger-rgb");
    root.style.removeProperty("--color-warning-rgb");
    root.style.removeProperty("--color-success-rgb");
    root.style.removeProperty("--color-info-rgb");
    root.style.removeProperty("--v-theme-primary");
    root.style.removeProperty("--v-theme-secondary");
    root.style.removeProperty("--v-theme-error");
    root.style.removeProperty("--v-theme-success");
    root.style.removeProperty("--v-theme-warning");
    root.style.removeProperty("--v-theme-info");
    root.style.removeProperty("--bs-danger");
    root.style.removeProperty("--bs-success");
    root.style.removeProperty("--bs-warning");
    root.style.removeProperty("--bs-info");
    root.style.removeProperty("--bs-body-bg");
    root.style.removeProperty("--bs-body-color");
    root.style.removeProperty("--bs-border-color");
    root.style.removeProperty("--bs-card-bg");
    root.style.removeProperty("--bs-secondary");
    root.style.removeProperty("--brand");
    root.style.removeProperty("--brand-d");
    root.style.removeProperty("--accent");
    root.style.removeProperty("--brand-rgb");
    root.style.removeProperty("--accent-rgb");
    root.style.removeProperty("--bg");
    root.style.removeProperty("--surface");
    root.style.removeProperty("--surface-2");
    root.style.removeProperty("--ink");
    root.style.removeProperty("--muted");
    root.style.removeProperty("--line");
  }

  try {
    window.dispatchEvent(
      new CustomEvent("theme-colors-updated", { detail: { ...next } })
    );
  } catch {
    /* ignore */
  }

  return next;
}

/** Apply + persist so live edits survive a later form reload / getEffectiveThemeColors. */
export function commitThemeColors(colors: ThemeColors): ThemeColors {
  const cached = persistThemeColorsCache(colors);
  return applyThemeColors(cached, { useInline: true });
}

export function applyStoredThemeColors(): ThemeColors {
  const stored = readStoredThemeColors();
  if (!stored) {
    return getDefaultThemeColors();
  }
  return applyThemeColors(
    { ...GRC_THEME_DEFAULTS, ...stored },
    { useInline: true }
  );
}

export async function loadThemeColorsFromGeneralSettings(): Promise<ThemeColors> {
  try {
    const { default: GeneralSettings } = await import(
      "@/API/GeneralSettings/GeneralSettings"
    );
    const api = new GeneralSettings();
    const rows = (await api.getAll({
      filter: `type|generalSettings|=`,
    })) as Array<Record<string, any>>;
    const mapped = mapRowToThemeColors(rows?.[0]);
    if (mapped) {
      persistThemeColorsCache(mapped);
      applyThemeColors(mapped, { useInline: true });
      return mapped;
    }
  } catch {
    /* fall through */
  }
  return getEffectiveThemeColors();
}

/**
 * Load theme colors without auth (login) via public-branding.
 * Prefers API; falls back to localStorage cache.
 * Same pattern as v4.1.5-tmo-system-colors.
 */
export async function loadPublicThemeColors(): Promise<ThemeColors> {
  if (publicLoadPromise) return publicLoadPromise;

  publicLoadPromise = (async () => {
    const fallback = getEffectiveThemeColors();
    try {
      const base = (process.env.VUE_APP_API_BASE_URL || "").replace(/\/?$/, "/");
      const response = await axios.get(`${base}system-setting/public-branding`, {
        transformRequest: [
          (data, headers) => {
            if (headers) {
              delete headers.Authorization;
              delete headers.authorization;
            }
            return data;
          },
        ],
      });
      const mapped = mapRowToThemeColors(response?.data);
      if (mapped) {
        persistThemeColorsCache(mapped);
        applyThemeColors(mapped, { useInline: true });
        return mapped;
      }
      // API reachable but no theme_colors — keep cache / defaults
      if (readStoredThemeColors()) {
        return applyThemeColors(fallback, { useInline: true });
      }
      return fallback;
    } catch {
      if (readStoredThemeColors()) {
        return applyThemeColors(fallback, { useInline: true });
      }
      return fallback;
    } finally {
      setTimeout(() => {
        publicLoadPromise = null;
      }, 0);
    }
  })();

  return publicLoadPromise;
}

/**
 * Save theme colors via API → DB (same flow as v4.1.5-tmo-system-colors),
 * then apply + cache. Throws on API failure so the UI can show the error.
 */
export async function saveThemeColorsToApi(
  colors: ThemeColors,
  settingsId?: string | number | null
): Promise<ThemeColors> {
  const next = persistThemeColorsCache(colors);
  applyThemeColors(next, { useInline: true });

  const { default: GeneralSettings } = await import(
    "@/API/GeneralSettings/GeneralSettings"
  );
  const api = new GeneralSettings();
  const payload: Record<string, any> = {
    theme_colors: toApiThemeColorsPayload(next),
  };
  if (settingsId !== null && settingsId !== undefined && settingsId !== "") {
    payload.id = String(settingsId);
  }

  const res = await api.saveThemeColors(payload);
  // success() envelope: { status:'success', data:{ ..., theme_colors:{...} } }
  const row = res?.data?.data || res?.data || res;
  const mapped = mapRowToThemeColors(row) || next;
  persistThemeColorsCache(mapped);
  applyThemeColors(mapped, { useInline: true });
  publicLoadPromise = null;
  return mapped;
}

export async function resetThemeColorsToDefaults(
  settingsId?: string | number | null
): Promise<ThemeColors> {
  const defaults = getDefaultThemeColors();
  try {
    await saveThemeColorsToApi(defaults, settingsId);
  } catch {
    clearStoredThemeColors();
    applyThemeColors(defaults, { useInline: false });
    try {
      window.dispatchEvent(
        new CustomEvent("theme-colors-updated", { detail: { ...defaults } })
      );
    } catch {
      /* ignore */
    }
    return defaults;
  }
  persistThemeColorsCache(defaults);
  applyThemeColors(defaults, { useInline: false });
  try {
    window.dispatchEvent(
      new CustomEvent("theme-colors-updated", { detail: { ...defaults } })
    );
  } catch {
    /* ignore */
  }
  return defaults;
}

export function cssColorToHex(value: string): string {
  const n = normalizeColor(value);
  if (n && n.startsWith("#")) return n.toUpperCase();
  const m = String(value)
    .trim()
    .match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (m) {
    return `#${[m[1], m[2], m[3]]
      .map((x) => Number(x).toString(16).padStart(2, "0"))
      .join("")
      .toUpperCase()}`;
  }
  return String(value).trim();
}

/** Resolve a CSS color / var() to `rgba(r,g,b,a)` for Chart.js / canvas. */
export function resolveCssColorToRgba(cssColor: string, alpha = 1): string {
  if (typeof document === "undefined") {
    const n = normalizeColor(cssColor);
    if (n && n.startsWith("#") && n.length === 7) {
      const r = parseInt(n.slice(1, 3), 16);
      const g = parseInt(n.slice(3, 5), 16);
      const b = parseInt(n.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return `rgba(68, 34, 92, ${alpha})`;
  }
  const el = document.createElement("div");
  el.style.color = cssColor;
  document.body.appendChild(el);
  const computed = getComputedStyle(el).color;
  document.body.removeChild(el);
  const m = computed.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (!m) return `rgba(68, 34, 92, ${alpha})`;
  return `rgba(${Math.round(Number(m[1]))}, ${Math.round(Number(m[2]))}, ${Math.round(
    Number(m[3])
  )}, ${alpha})`;
}

/** Default chart palette driven by live theme tokens. */
export function getThemeChartPalette(alpha = 1): string[] {
  const keys = [
    "var(--color-primary)",
    "var(--color-danger)",
    "var(--color-warning)",
    "var(--color-success)",
    "var(--color-gray)",
    "var(--color-info)",
    "var(--color-hover)",
    "var(--color-pressed)",
    "var(--color-text-secondary)",
    "var(--color-info-soft)",
  ];
  return keys.map((k) => resolveCssColorToRgba(k, alpha));
}

/**
 * ABER-style token aliases used by LDAP (and similar) pages.
 * Mapped onto GRC system colors so UI follows General Settings / primary.
 */
const ABER_TOKEN_TO_GRC: Record<string, ThemeColorKey> = {
  brand: "primary",
  "brand-d": "pressed",
  accent: "primary",
  gold: "warning",
  bg: "bg",
  surface: "surface",
  "surface-2": "surface-2",
  ink: "text-primary",
  muted: "text-secondary",
  line: "border",
};

function resolveAberOrGrcKey(key: string): ThemeColorKey {
  if ((THEME_COLOR_KEYS as string[]).includes(key)) {
    return key as ThemeColorKey;
  }
  return ABER_TOKEN_TO_GRC[key] || "primary";
}

/** Runtime CSS token value for charts / JS (LDAP pages use brand/accent). */
export function cssToken(key: string): string {
  if (typeof document !== "undefined") {
    const aberVar = key.startsWith("--") ? key : `--${key}`;
    const liveAber = getComputedStyle(document.documentElement)
      .getPropertyValue(aberVar)
      .trim();
    if (liveAber) return liveAber;

    const grcKey = resolveAberOrGrcKey(key);
    const liveGrc = getComputedStyle(document.documentElement)
      .getPropertyValue(cssVarName(grcKey))
      .trim();
    if (liveGrc) return liveGrc;
  }
  const grcKey = resolveAberOrGrcKey(key);
  return getEffectiveThemeColors()[grcKey];
}

export function cssTokenRgb(key: string): string {
  const hex = cssToken(key);
  return hexToRgbChannels(hex) || "68,34,92";
}

